import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, FormGroup, Input, Label } from "reactstrap";
import { Btn, H2, H6, P, Image } from "../AbstractElements";
import { dynamicImage } from "../Utils";
import {
    CreateYourPassword,
    Done,
    EnterOtp,
    Href,
    NewPassword,
    ResetYourPassword,
    RetypePassword,
    Send,
    SignIn,
    Resend,
} from "../Utils/Constants";
import axios from "axios";
import { baseUrl, mediaUrl } from "../Utils";
import { toast, ToastContainer } from "react-toastify";
import { useConfig } from "../Context/ConfigContext";

export default function ForgetPassword() {
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [loading, setLoading] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const navigate = useNavigate();
    const [timer, setTimer] = useState<number>(60);
    const [otpStartTime, setOtpStartTime] = useState<number | null>(null);
    const { config } = useConfig();
    const logoSrc = config.logo ? `${mediaUrl}${config.logo}` : dynamicImage(`logo/logo1.png`);

    const [formData, setFormData] = useState({
        email: "",
        otp: ["", "", "", "", "", ""],
        newPassword: "",
        retypePassword: "",
    });

    const otpInputRefs = useRef<HTMLInputElement[]>([]);

    const toggle = () => setPasswordVisible(!isPasswordVisible);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index?: number
    ) => {
        const { id, value } = e.target;

        if (index !== undefined) {
            if (/^[0-9]?$/.test(value)) {
                const newOtp = [...formData.otp];
                newOtp[index] = value;
                setFormData((prev) => ({ ...prev, otp: newOtp }));

                if (value && index < 5) {
                    otpInputRefs.current[index + 1]?.focus();
                }
            }
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [id]: value,
            }));
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (e.key === "Backspace") {
            if (formData.otp[index] === "" && index > 0) {
                otpInputRefs.current[index - 1]?.focus();
            }
        }
    };

    useEffect(() => {
        let interval: any;
        if (otpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [otpSent, timer]);

    const sendOtp = async () => {
        if (!formData.email) {
            toast.error("Please enter email");
            return;
        }
        try {
            setLoading(true);
            const res = await axios.post(`${baseUrl}send-otp`, {
                email: formData.email,
            });
            toast.success(res.data?.message || "OTP sent to your email.");
            setOtpSent(true);
            setOtpVerified(false);
            setFormData((prev) => ({ ...prev, otp: ["", "", "", "", "", ""] }));

            setOtpStartTime(Date.now());
            setTimer(60);

            setTimeout(() => {
                otpInputRefs.current[0]?.focus();
            }, 100);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    const verifyOtp = async () => {
        try {
            setOtpVerifying(true);
            const otp = formData.otp.join("");
            await axios.post(`${baseUrl}verify-otp`, {
                email: formData.email,
                otp,
            });
            toast.success("OTP verified successfully.");
            setOtpVerified(true);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Invalid or expired OTP.");
            setOtpVerified(false);
        } finally {
            setOtpVerifying(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (formData.newPassword !== formData.retypePassword) {
            toast.error("Passwords do not match");
            return;
        }
        try {
            setResettingPassword(true);
            const otp = formData.otp.join("");
            await axios.post(`${baseUrl}reset-password`, {
                email: formData.email,
                otp,
                password: formData.newPassword,
                password_confirmation: formData.retypePassword,
            });
            toast.success("Password reset successfully!");
            setTimeout(() => navigate("/login"), 2000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to reset password.");
        } finally {
            setResettingPassword(false);
        }
    };

    return (
        <div className="page-wrapper">
            <Container fluid className="p-0">
                <Row>
                    <Col xs={12}>
                        <div className="login-card login-dark">
                            <div>
                                <div className="text-center mb-4">
                                    <Link className="logo" to={`/dashboard`}>
                                        <Image
                                            className="img-fluid for-light m-auto"
                                            src={logoSrc}
                                            alt="loginpage"
                                        />
                                    </Link>
                                </div>
                                <div className="login-main">
                                    <Form className="theme-form" onSubmit={handleSubmit}>
                                        <H2>{ResetYourPassword}</H2>

                                        <FormGroup>
                                            <Label className="pt-0">Enter your email</Label>
                                            <Input
                                                type="email"
                                                id="email"
                                                value={formData.email}
                                                placeholder="Email"
                                                onChange={handleInputChange}
                                                required
                                                disabled={otpSent || otpVerified}
                                            />
                                            <div className="text-end mt-2">
                                                <Btn
                                                    color="primary"
                                                    block
                                                    className="m-t-10"
                                                    onClick={sendOtp}
                                                    type="button"
                                                    disabled={otpVerified}
                                                >
                                                    {loading ? "Sending..." : Send}
                                                </Btn>
                                            </div>
                                        </FormGroup>

                                        {otpSent && !otpVerified && (
                                            <div className="mt-4 mb-4 text-center">
                                                <span className="reset-password-link">
                                                    {"Didn't receive OTP? "}
                                                    <a
                                                        className="btn-link text-danger"
                                                        href={Href}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            sendOtp();
                                                        }}
                                                    >
                                                        {loading ? "Sending..." : Resend}
                                                    </a>
                                                </span>

                                                {timer > 0 ? (
                                                    <div className="text-danger mt-2">
                                                        OTP expires in {timer}s
                                                    </div>
                                                ) : (
                                                    <div className="text-danger mt-2 font-weight-bold">
                                                        OTP Expired! Please resend.
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <FormGroup>
                                            <Label className="pt-0">{EnterOtp}</Label>
                                            <Row>
                                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                                    <Col key={i} xs={2}>
                                                        <Input
                                                            className="text-center opt-text"
                                                            type="text"
                                                            maxLength={1}
                                                            value={formData.otp[i]}
                                                            onChange={(e) => handleInputChange(e, i)}
                                                            onKeyDown={(e) => handleKeyDown(e, i)}
                                                            disabled={!otpSent || otpVerified || timer === 0}
                                                            innerRef={(el) =>
                                                            (otpInputRefs.current[i] =
                                                                el as HTMLInputElement)
                                                            }
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                            <div className="text-end mt-2">
                                                <Btn
                                                    color="primary"
                                                    className="m-t-10 w-100"
                                                    onClick={verifyOtp}
                                                    type="button"
                                                    block
                                                    disabled={!otpSent || otpVerifying || otpVerified || timer === 0}
                                                >
                                                    {otpVerifying ? "Verifying..." : "Verify OTP"}
                                                </Btn>
                                            </div>
                                        </FormGroup>

                                        <H6 className="mt-4 f-w-700">{CreateYourPassword}</H6>

                                        <FormGroup>
                                            <Label>{NewPassword}</Label>
                                            <div className="form-input position-relative">
                                                <Input
                                                    type={isPasswordVisible ? "text" : "password"}
                                                    id="newPassword"
                                                    value={formData.newPassword}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="*********"
                                                    disabled={!otpVerified}
                                                />
                                                <div className="show-hide" onClick={toggle}>
                                                    <span
                                                        className={!isPasswordVisible ? "show" : ""}
                                                    ></span>
                                                </div>
                                            </div>
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>{RetypePassword}</Label>
                                            <div className="form-input position-relative">
                                                <Input
                                                    type={isPasswordVisible ? "text" : "password"}
                                                    id="retypePassword"
                                                    value={formData.retypePassword}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="*********"
                                                    disabled={!otpVerified}
                                                />
                                                <div className="show-hide" onClick={toggle}></div>
                                            </div>
                                        </FormGroup>

                                        <Btn
                                            type="submit"
                                            color="primary"
                                            className="w-100 mt-3"
                                            block
                                            disabled={resettingPassword || !otpVerified}
                                        >
                                            {resettingPassword ? "Resetting..." : Done}
                                        </Btn>

                                        <P className="mt-4 mb-0 text-center">
                                            Already have an account?
                                            <Link className="ms-2" to={`/login`}>
                                                {SignIn}
                                            </Link>
                                        </P>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
        </div>
    );
}
